import useDispatchConfirmation from "./useDispatchConfirmation";
import React, { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { api } from "./api";
import {
  Field,
  Lookup,
  Select,
  Pager,
  Status,
  DESTINATION_TYPES,
  useData,
} from "./common";
const split = (s) =>
  String(s || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
export default function Catalog({ kind, canEdit }) {
  const { confirm, dialog: confirmationDialog } = useDispatchConfirmation();
  const items = kind === "insumos";
  const [q, setQ] = useState(""),
    [search, setSearch] = useState(""),
    [active, setActive] = useState(""),
    [page, setPage] = useState(1),
    [revision, setRevision] = useState(0),
    [form, setForm] = useState(null),
    [operator, setOperator] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const state = useData(kind, { q: search, active, page }, revision);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const open = (row) => {
    setError("");
    setForm(
      row
        ? {
            ...row,
            aliases: row.aliases?.join(", "),
            pendingData: row.pendingData?.join(", "),
          }
        : items
        ? {
            code: "",
            name: "",
            presentation: "",
            category: "REVENTA",
            dispatchUnit: "BOLSA",
            allowDecimals: false,
          }
        : { code: "", name: "", type: "RUTA", aliases: "" }
    );
    setOperator(
      row?.operatorId
        ? { _id: row.operatorId, name: row.operatorName || "Titular asignado" }
        : null
    );
  };
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { code: form.code, name: form.name };
      if (items) {
        const hasContent = form.netContent !== "" && form.netContent != null;
        if (hasContent !== !!form.contentUnit)
          throw new Error(
            "Indique el contenido neto y su unidad juntos, o deje ambos vacíos."
          );
        Object.assign(body, {
          presentation: form.presentation,
          category: form.category,
          dispatchUnit: form.dispatchUnit,
          allowDecimals: !!form.allowDecimals,
          pendingData: split(form.pendingData),
          netContent:
            form.netContent === "" || form.netContent == null
              ? form._id
                ? null
                : undefined
              : Number(form.netContent),
          contentUnit: form.contentUnit || (form._id ? "" : undefined),
        });
        for (const k of [
          "quickBooksItemId",
          "quickBooksRealmId",
          "quickBooksBusinessUnit",
        ])
          body[k] = form[k]?.trim() || null;
      } else {
        if (typeof operator === "string" && operator.trim())
          throw new Error("Seleccione un usuario de la lista.");
        Object.assign(body, {
          type: form.type,
          aliases: split(form.aliases),
          operatorId: form.type === "RUTA" ? operator?._id || null : null,
        });
      }
      if (form._id) body.version = form.version;
      await api(
        kind + (form._id ? "/" + form._id : ""),
        form._id ? "PATCH" : "POST",
        body
      );
      setForm(null);
      setRevision(revision + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (row) => {
    if (
      !(await confirm({
        message: `${row.active ? "Desactivar" : "Activar"} ${row.name}?`,
      }))
    )
      return;
    setBusy(true);
    setError("");
    try {
      await api(`${kind}/${row._id}/estado`, "PATCH", {
        active: !row.active,
        version: row.version,
      });
      setRevision(revision + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      {confirmationDialog}
      <h2>{items ? "Catálogo de insumos" : "Destinos"}</h2>
      <div className="sd-toolbar">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(q);
            setPage(1);
          }}
        >
          <input
            aria-label="Buscar catálogo"
            placeholder="Código, nombre o alias"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button type="submit" label="Buscar" />
        </form>
        <Select
          title="Estado"
          value={active}
          options={[
            { value: "true", label: "Activos" },
            { value: "false", label: "Inactivos" },
          ]}
          onChange={(v) => {
            setActive(v);
            setPage(1);
          }}
          empty="Todos"
        />
        {canEdit && (
          <Button
            label={items ? "Nuevo insumo" : "Nuevo destino"}
            onClick={() => open(null)}
          />
        )}
      </div>
      <Status {...state} error={state.error || error} />
      <DataTable
        className="sd-sticky-table"
        value={state.data?.rows || []}
        dataKey="_id"
        responsiveLayout="scroll"
        emptyMessage="Sin registros"
      >
        <Column field="code" header="Código" />
        <Column field="name" header="Nombre" />
        {items ? (
          <Column field="presentation" header="Presentación" />
        ) : (
          <Column field="type" header="Tipo" />
        )}
        {items ? (
          <Column field="dispatchUnit" header="Unidad" />
        ) : (
          <Column field="operatorName" header="Titular" />
        )}
        <Column
          header="Pendientes / alias"
          body={(r) => (items ? r.pendingData : r.aliases)?.join(", ")}
        />
        <Column
          header="Estado"
          body={(r) => (r.active ? "Activo" : "Inactivo")}
        />
        {canEdit && (
          <Column
            header="Acciones"
            body={(r) => (
              <div className="sd-toolbar">
                <Button
                  label="Editar"
                  disabled={busy}
                  onClick={() => open(r)}
                />
                <Button
                  label={r.active ? "Desactivar" : "Activar"}
                  disabled={busy}
                  onClick={() => toggle(r)}
                />
              </div>
            )}
          />
        )}
      </DataTable>
      {state.data && <Pager data={state.data} page={page} setPage={setPage} />}
      <Dialog
        header={form?._id ? "Editar catálogo" : "Nuevo registro"}
        visible={!!form}
        onHide={() => {
          if (!busy) setForm(null);
        }}
        style={{ width: "55rem", maxWidth: "95vw" }}
      >
        {form && (
          <>
            <fieldset disabled={busy} className="sd-form">
              <div className="sd-grid">
                <Field title="Código">
                  <input
                    value={form.code}
                    disabled={
                      !!form._id &&
                      (!items || form.codeLocked || form.codeReservations > 0)
                    }
                    onChange={(e) => set("code", e.target.value)}
                  />
                </Field>
                <Field title="Nombre">
                  <input
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                  />
                </Field>
                {items ? (
                  <>
                    <Field title="Presentación">
                      <input
                        value={form.presentation}
                        onChange={(e) => set("presentation", e.target.value)}
                      />
                    </Field>
                    <Select
                      title="Categoría"
                      value={form.category}
                      options={[
                        "MATERIA_PRIMA",
                        "PRODUCTO_TERMINADO",
                        "REVENTA",
                      ]}
                      onChange={(v) => set("category", v)}
                    />
                    <Select
                      title="Unidad de despacho"
                      value={form.dispatchUnit}
                      options={["BOLSA", "PAQUETE", "CAJA", "UNIDAD"]}
                      onChange={(v) => set("dispatchUnit", v)}
                    />
                    <Field title="Contenido neto (opcional)">
                      <input
                        type="number"
                        step="any"
                        value={form.netContent ?? ""}
                        onChange={(e) => set("netContent", e.target.value)}
                      />
                    </Field>
                    <Select
                      title="Unidad de contenido"
                      value={form.contentUnit}
                      options={["G", "UNIDADES"]}
                      onChange={(v) => set("contentUnit", v)}
                    />
                    <Field title="Permite decimales">
                      <input
                        type="checkbox"
                        checked={!!form.allowDecimals}
                        onChange={(e) => set("allowDecimals", e.target.checked)}
                      />
                    </Field>
                    <Field title="Datos pendientes (separados por comas)">
                      <input
                        value={form.pendingData || ""}
                        onChange={(e) => set("pendingData", e.target.value)}
                      />
                    </Field>
                    <Field title="ID del ítem QuickBooks (opcional)">
                      <input
                        value={form.quickBooksItemId || ""}
                        onChange={(e) =>
                          set("quickBooksItemId", e.target.value)
                        }
                      />
                    </Field>
                    <Field title="Empresa QuickBooks (realm ID)">
                      <input
                        value={form.quickBooksRealmId || ""}
                        onChange={(e) =>
                          set("quickBooksRealmId", e.target.value)
                        }
                      />
                    </Field>
                    <Field title="Unidad de negocio QuickBooks">
                      <input
                        value={form.quickBooksBusinessUnit || ""}
                        onChange={(e) =>
                          set("quickBooksBusinessUnit", e.target.value)
                        }
                      />
                    </Field>
                  </>
                ) : (
                  <>
                    <Select
                      title="Tipo de destino"
                      value={form.type}
                      options={DESTINATION_TYPES}
                      onChange={(v) => set("type", v)}
                    />
                    <Field title="Alias (separados por comas)">
                      <input
                        value={form.aliases || ""}
                        onChange={(e) => set("aliases", e.target.value)}
                      />
                    </Field>
                    {form.type === "RUTA" && (
                      <Lookup
                        title="Titular (cualquier usuario activo)"
                        path="operadores"
                        value={operator}
                        onChange={setOperator}
                      />
                    )}
                  </>
                )}
              </div>
            </fieldset>
            <Status error={error} />
            <Button label="Guardar" loading={busy} onClick={save} />
          </>
        )}
      </Dialog>
    </section>
  );
}
